// src/utils/versionCompare.ts
//
// Pure, dependency-free version-string comparison. Extracted from
// `user_dashboard_components/utils.ts` so both the user dashboard's outdated icon and the
// Insights version-currency indicator share one comparator instead of two independently
// maintained copies. Must not import from `views/` or `insights/` - this is a low-level
// shared module.

const parseToInt = (segment: string) => {
  const number = parseInt(String(segment ?? '0'), 10);
  return Number.isFinite(number) ? number : 0;
};

/**
 * Three-way comparison of two dotted version strings, suitable for `Array.prototype.sort`.
 * Segments are compared left to right as integers, and a missing trailing segment counts as
 * 0, so "1.8" and "1.8.0" compare equal. A non-numeric segment also parses to 0 - callers
 * that need a non-version sentinel (e.g. "Unknown") to rank deterministically must special-
 * case it themselves rather than relying on that fallback.
 * @param {string} a
 * @param {string} b
 * @returns {number} negative if `a` is a lower version than `b`, positive if higher, 0 if equal
 */
export function compareVersions(a: string, b: string): number {
  const aSegments = String(a).split('.').map(parseToInt);
  const bSegments = String(b).split('.').map(parseToInt);

  const maxLength = Math.max(aSegments.length, bSegments.length);

  for (let index = 0; index < maxLength; index++) {
    const aValue = aSegments[index] ?? 0;
    const bValue = bSegments[index] ?? 0;

    if (aValue !== bValue) return aValue - bValue;
  }

  return 0; // versions are equal
}

/**
 * Check if the current version string is behind the latest version string.
 * Handles dotted versions like "2.413" vs "2.034" or "1.4.2.0" vs "1.4.4.0".
 * @param {string | null} currentVersion
 * @param {string | null} latestVersion
 */
export function isVersionOutdated(
  currentVersion: string | null,
  latestVersion: string | null,
) {
  if (!currentVersion || !latestVersion) return false;

  // Strictly lower → outdated. Equal or ahead → not outdated.
  return compareVersions(currentVersion, latestVersion) < 0;
}
